import React from "react";

import { useTheme } from "@mui/material/styles";
import { getSettings } from "@services/configService";

import emteriaLogoFullSvg from "./emteria-logo-full.svg";
import emteriaLogoIcon from "./emteria-logo-small.png";
import jkcLogoFullSvg from "./jvc-logo-full.svg";
import vodafoneLogoFullSvg from "./vodafone-logo-full.svg";

type LogoConfig = {
    full: {
        img: string;
        alt: string;
        size: {
            small: [number, number];
            default: [number, number];
        };
    };
    icon: {
        img: string | null;
        alt: string;
        size: {
            small: [number, number] | [];
            default: [number, number];
        };
    } | null;
};

const ImageMap: Record<string, LogoConfig> = {
    emteria: {
        full: {
            img: emteriaLogoFullSvg,
            alt: "emteria full logo",
            size: {
                default: [240, 59],
                small: [170, 32],
            },
        },
        icon: {
            img: emteriaLogoIcon,
            alt: "emteria icon logo",
            size: {
                default: [29, 32],
                small: [],
            },
        },
    },
    jkc: {
        full: {
            img: jkcLogoFullSvg,
            alt: "jkc full logo",
            size: {
                default: [195, 59],
                small: [170, 32],
            },
        },
        icon: {
            img: null,
            alt: "jkc icon logo",
            size: {
                default: [29, 32],
                small: [],
            },
        },
    },
    vodafone: {
        full: {
            img: vodafoneLogoFullSvg,
            alt: "vodafone full logo",
            size: {
                default: [240, 59],
                small: [200, 50],
            },
        },
        icon: {
            img: null,
            alt: "vodafone icon logo",
            size: {
                default: [29, 32],
                small: [],
            },
        },
    },
};

type Props = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
    drawerOpen?: boolean;
    variant?: "full" | "icon";
};

export const Logo = ({ drawerOpen, variant, className = "", ...props }: Props) => {
    const theme = useTheme();
    const { features } = getSettings();
    const logoConfig = ImageMap[features.logo];

    let selectedVariant;
    let size: "small" | "default";

    if (variant === "full") {
        selectedVariant = logoConfig.full;
        size = "default";
    } else if (variant === "icon") {
        selectedVariant = logoConfig.icon;
        size = "default";
    } else {
        selectedVariant = !drawerOpen ? logoConfig.icon : logoConfig.full;
        size = !drawerOpen ? "default" : "small";
    }

    if (!selectedVariant || !selectedVariant.img) {
        return null;
    }

    const imageSize = selectedVariant.size[size];

    const combinedClassName = React.useMemo(() => {
        if (className.includes("d-inline-block")) {
            return className;
        }
        const defaultClasses = "d-inline-block align-top";
        return className ? `${defaultClasses} ${className}` : defaultClasses;
    }, [className]);

    return (
        <img
            src={selectedVariant.img}
            alt={selectedVariant.alt}
            width={imageSize[0] || undefined}
            height={imageSize[1] || undefined}
            className={combinedClassName}
            style={{ filter: theme.palette.mode === "dark" ? "brightness(0) invert(1)" : "none" }}
            {...props}
        />
    );
};
